import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import HttpsRedirect from 'react-https-redirect';
import mixpanel from 'mixpanel-browser';
import { hotjar } from 'react-hotjar';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { Provider } from 'react-redux';
// import store from './redux/configureStore';
import store from './app/store'
import { ThemeProvider } from '@material-ui/core/styles';
import { initPostHog, identifyUserFromMe } from './utils/posthog';
import { getBrandingTheme, shouldUseDarkMode, getMetaThemeColor } from './utils/brandingTheme';
import {
  applyDashboardAuthFromUrl,
  ensureValidAuthSession,
} from './utils/dashboardAuthParams';

applyDashboardAuthFromUrl();

const url = window.location.origin
if (!url.includes('workload') && !url.includes('https')) {
  window.location = `https:${url.split(':')[1]}`
}

// Theme wrapper component that updates based on time
const ThemedApp = () => {
  const [theme, setTheme] = useState(() => getBrandingTheme());
  const [darkMode, setDarkMode] = useState(shouldUseDarkMode());

  // Update theme when dark mode should change (at 6pm or 7am)
  useEffect(() => {
    const updateTheme = () => {
      const shouldBeDark = shouldUseDarkMode();
      if (shouldBeDark !== darkMode) {
        setDarkMode(shouldBeDark);
        setTheme(getBrandingTheme());
        // Update meta theme color for browser
        const metaThemeColor = document.querySelector('meta[name="theme-color"]');
        if (metaThemeColor) {
          metaThemeColor.setAttribute('content', getMetaThemeColor(shouldBeDark ? 'dark' : 'light'));
        }
      }
    };

    // Check immediately
    updateTheme();

    // Set up interval to check every minute
    const interval = setInterval(updateTheme, 60000);

    // Also check when the hour changes (at :00 of each hour)
    const now = new Date();
    const msUntilNextHour = (60 - now.getMinutes()) * 60 * 1000 - now.getSeconds() * 1000 - now.getMilliseconds();
    
    const hourChangeTimeout = setTimeout(() => {
      updateTheme();
      // Then check every hour
      const hourlyInterval = setInterval(updateTheme, 60 * 60 * 1000);
      return () => clearInterval(hourlyInterval);
    }, msUntilNextHour);

    return () => {
      clearInterval(interval);
      clearTimeout(hourChangeTimeout);
    };
  }, [darkMode]);

  // Update meta theme color and CSS variables on mount and theme changes
  useEffect(() => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]') || document.createElement('meta');
    if (!metaThemeColor.hasAttribute('name')) {
      metaThemeColor.setAttribute('name', 'theme-color');
      document.head.appendChild(metaThemeColor);
    }
    metaThemeColor.setAttribute('content', getMetaThemeColor(darkMode ? 'dark' : 'light'));
    
    // Set CSS variables for theme colors on root element
    const root = document.documentElement;
    root.style.setProperty('--theme-background-paper', theme.palette.background.paper);
    root.style.setProperty('--theme-background-default', theme.palette.background.default);
    root.style.setProperty('--theme-text-primary', theme.palette.text.primary);
    root.style.setProperty('--theme-text-secondary', theme.palette.text.secondary);
    root.style.setProperty('--theme-divider', theme.palette.divider);
    root.style.setProperty('--theme-primary', theme.palette.primary.main);
    root.style.setProperty('--theme-action-hover', theme.palette.type === 'dark' ? theme.palette.action.hover : '#f3f4f6');
    // CodeMirror CSS variables
    root.style.setProperty('--codemirror-background', theme.palette.background.paper);
    root.style.setProperty('--codemirror-text', theme.palette.text.primary);
    root.style.setProperty('--codemirror-border', theme.palette.type === 'dark' ? theme.palette.divider : '#ddd');
    root.style.setProperty('--codemirror-gutter-bg', theme.palette.type === 'dark' ? theme.palette.action.hover : '#f7f7f7');
    root.style.setProperty('--codemirror-cursor', theme.palette.text.primary);
    root.style.setProperty('--codemirror-selection', theme.palette.type === 'dark' ? 'rgba(0, 105, 234, 0.3)' : '#d7d4f0');
  }, [darkMode, theme]);

  return (
    <ThemeProvider theme={theme}>
      <App />
    </ThemeProvider>
  );
};

ReactDOM.render(
 
    <HttpsRedirect>
      <Provider store={store}>
        <ThemedApp />
      </Provider>
    </HttpsRedirect>,
  document.getElementById('root')

);

// If you want to start measuring performance in your app, pass a function
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
mixpanel.init('d410d8313a8736e962ffad2cfb6f4570');
//hotjar.initialize(2768116, 6);

// Initialize PostHog
initPostHog();

// If /api/auth/me is not 200 (expired JWT), bounce to Automate sign-in with next=
// so scaffold can mint a fresh token and return here with ?c=&u=&t=.
ensureValidAuthSession().then(({ ok, user }) => {
  if (!ok) return;
  if (user) {
    identifyUserFromMe(user);
  }
});